/**
 * Timeline gridlines, ticks and labels (MapTimeline.vue + MapTimeItem.vue).
 *
 * Granularity is driven by zoom, with three header tiers (top y=60, middle y=40,
 * bottom y=20 measured up from START_Y). The visible tiers per zoom band mirror
 * the SVG thresholds: days appear above 10×, weeks at/above 2×, months at/above
 * 1×, quarters in 0.2–1×, years otherwise. Day labels only show above 20× (with
 * weekday above 60×); week labels above 3×. Weekends and public holidays are
 * shaded with a diagonal hatch once days are visible.
 *
 * `drawTimeline` is the calendar timeline (projects). Templates have no dates and
 * use `drawTemplateTimeline` (below) — a plain position counter.
 */
import Holidays from 'date-holidays';
import { DateTime } from 'luxon';
import { START_Y } from '@/store/remapStore';
import { daysBetweenDates } from '@/utils/helpers/datetime';
import { cssVar } from '../../draw/palette';
import { hatchPattern } from '../../draw/shapes';
import type { SceneContext } from '../../engine/SceneContext';

// Month / weekday labels are localised through the same i18n keys the SVG
// timeline uses (`MapTimeline.vue` monthNames / dayAbbreviations) — NOT Luxon's
// `toFormat('LLL')` / `weekdayShort`, which always render in English. Indexed by
// Luxon's `month` (1–12) and `weekday` (1=Mon … 7=Sun).
const MONTH_KEYS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];
// Indexed by JS `getDay()` order (0=Sun … 6=Sat), matching the SVG array.
const WEEKDAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

/**
 * Month-tier caption (MapTimeline `trimMonthName` + the `showDays ? year` suffix):
 * the localised month name, trimmed to 3 / 1 chars as 90 days narrows on screen
 * (`scaleX(90)` = 90 × zoom) so labels don't overlap at low zoom, with the year
 * appended once days are visible (the deepest tier).
 */
function monthCaption(d: DateTime, zoom: number, withYear: boolean): string {
  const name = $t(`monthNames.${MONTH_KEYS[d.month - 1]}`);
  const width = 90 * zoom;
  const trimmed =
    width > 200 ? name : width > 110 ? name.slice(0, 3) : name.slice(0, 1);

  return withYear ? `${trimmed} ${d.year}` : trimmed;
}

function weekdayLabel(d: DateTime): string {
  // Luxon weekday 1..7 (Mon..Sun) → getDay 0..6 (Sun..Sat): Sun(7) % 7 = 0.
  return $t(`weekday.${WEEKDAY_KEYS[d.weekday % 7]}`);
}

export function drawTimeline(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
): void {
  const { map, io } = scene;
  const project = scene.project.project(map.mapId);
  if (!project?.start_date) {
    return;
  }
  const start = DateTime.fromISO(project.start_date).startOf('day');
  const totalDays = io.mapLength;
  const zoom = map.zoom;

  // Only walk the VISIBLE day range, not the whole project. At the days tier a
  // multi-year map would otherwise build a label ($t weekday lookup) and project
  // every single day each frame — O(mapLength) work that's almost all culled by
  // x. Bounding to [firstVisiblePos, lastVisiblePos] makes the timeline O(visible).
  const visFirst = Math.max(0, Math.floor(map.firstVisiblePos) - 1);
  const visLast = Math.min(totalDays, Math.ceil(map.lastVisiblePos) + 1);
  const rangeStart = start.plus({ days: visFirst });
  const rangeEnd = start.plus({ days: visLast });

  const gridColor = cssVar('--color-gray-200', '#E4E7EC');
  const darkColor = cssVar('--color-gray-400', '#98A2B3');
  const labelColor = cssVar('--color-gray-500', '#667085');

  const minX = -50;
  const maxX = scene.width + 50;

  const drawMarker = (
    pos: number,
    label: string,
    yLevel: number,
    dark = false,
  ): void => {
    const x = scene.screenX(pos) + 0.5;
    if (x < minX || x > maxX) {
      return;
    }
    const stroke = dark ? darkColor : gridColor;
    // Vertical gridline through the content area. Year markers (dark=true) use
    // darkColor so they paint last and aren't overwritten by lighter month lines.
    ctx.beginPath();
    ctx.moveTo(x, START_Y);
    ctx.lineTo(x, scene.height);
    ctx.lineWidth = 1;
    ctx.strokeStyle = stroke;
    ctx.stroke();
    // Short tick mark in the header.
    ctx.beginPath();
    ctx.moveTo(x, START_Y - yLevel);
    ctx.lineTo(x, START_Y - yLevel + 10);
    ctx.strokeStyle = stroke;
    ctx.stroke();
    if (label) {
      ctx.fillStyle = labelColor;
      ctx.font = '10px Roboto, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(label, x + 5, START_Y - yLevel + 9);
    }
  };

  const forEachStep = (
    unit: 'years' | 'quarters' | 'months' | 'weeks' | 'days',
    fn: (d: DateTime) => void,
  ): void => {
    // Align the unit's first tick to the boundary at/just before the visible
    // start (so a partly-scrolled month/week still shows its label), then iterate
    // only to the visible end.
    const startOf =
      unit === 'years'
        ? rangeStart.startOf('year')
        : unit === 'quarters'
          ? rangeStart.startOf('quarter')
          : unit === 'months'
            ? rangeStart.startOf('month')
            : unit === 'weeks'
              ? rangeStart.startOf('week')
              : rangeStart;
    const step =
      unit === 'years'
        ? { years: 1 }
        : unit === 'quarters'
          ? { quarters: 1 }
          : unit === 'months'
            ? { months: 1 }
            : unit === 'weeks'
              ? { weeks: 1 }
              : { days: 1 };
    for (let d = startOf; d <= rangeEnd; d = d.plus(step)) {
      const pos = daysFrom(start, d);
      if (pos >= 0 && pos <= totalDays) {
        fn(d);
      }
    }
  };

  if (zoom > 10) {
    drawShading(ctx, scene, start, visFirst, visLast);
    forEachStep('days', (d) =>
      drawMarker(
        daysFrom(start, d),
        zoom > 20
          ? zoom > 60
            ? `${d.day} ${weekdayLabel(d)}`
            : `${d.day}`
          : '',
        20,
      ),
    );
    forEachStep('weeks', (d) =>
      drawMarker(daysFrom(start, d), zoom > 3 ? `W${d.weekNumber}` : '', 40),
    );
    forEachStep('months', (d) =>
      drawMarker(daysFrom(start, d), monthCaption(d, zoom, true), 60, true),
    );
  } else if (zoom >= 2) {
    forEachStep('weeks', (d) =>
      drawMarker(daysFrom(start, d), zoom > 3 ? `W${d.weekNumber}` : '', 20),
    );
    forEachStep('months', (d) =>
      drawMarker(daysFrom(start, d), monthCaption(d, zoom, false), 40, true),
    );
    forEachStep('years', (d) =>
      drawMarker(daysFrom(start, d), String(d.year), 60, true),
    );
  } else if (zoom >= 1) {
    forEachStep('months', (d) =>
      drawMarker(daysFrom(start, d), monthCaption(d, zoom, false), 40),
    );
    forEachStep('years', (d) =>
      drawMarker(daysFrom(start, d), String(d.year), 60, true),
    );
  } else if (zoom >= 0.2) {
    forEachStep('quarters', (d) =>
      drawMarker(daysFrom(start, d), `Q${d.quarter}`, 40),
    );
    forEachStep('years', (d) =>
      drawMarker(daysFrom(start, d), String(d.year), 60, true),
    );
  } else {
    forEachStep('years', (d) =>
      drawMarker(daysFrom(start, d), String(d.year), 60, true),
    );
  }
}

/**
 * Template timeline (MapTimelineTemplate.vue + MapTimeItem.vue): a position
 * counter (0…mapLength) instead of calendar dates. Templates have no real dates,
 * so each integer position gets a single bottom-tier tick (y=20), a full-height
 * gridline and — once zoomed in far enough — its index as the label. Shown only
 * in the 'positions' template mode (the 'none' mode draws no timeline at all),
 * and only above 10× zoom (SVG `showDays`), with labels above 20× (SVG showLabels).
 */
export function drawTemplateTimeline(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
): void {
  const { map, io } = scene;
  if (map.timeLineTemplateMode !== 'positions' || map.zoom <= 10) {
    return;
  }
  const showLabels = map.zoom > 20;
  const gridColor = cssVar('--color-gray-200', '#E4E7EC');
  const labelColor = cssVar('--color-gray-500', '#667085');
  const first = Math.max(0, map.firstVisiblePos);
  const last = Math.min(io.mapLength, map.lastVisiblePos);

  ctx.lineWidth = 1;
  ctx.strokeStyle = gridColor;
  ctx.font = '10px Roboto, sans-serif';
  ctx.fillStyle = labelColor;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';

  for (let pos = first; pos < last; pos++) {
    const x = scene.screenX(pos) + 0.5;
    // Bottom-tier header tick (position 'bottom' → y=20 up from START_Y).
    ctx.beginPath();
    ctx.moveTo(x, START_Y - 20);
    ctx.lineTo(x, START_Y - 10);
    ctx.stroke();
    // Full-height gridline through the content area.
    ctx.beginPath();
    ctx.moveTo(x, START_Y);
    ctx.lineTo(x, scene.height);
    ctx.stroke();
    if (showLabels) {
      ctx.fillText(String(pos), x + 5, START_Y - 11);
    }
  }
}

/**
 * Weekend (Sat/Sun) + public-holiday + custom-day-off shading under the timeline,
 * shown when days are visible. All three use the same diagonal gray hatch
 * (MapTimeline's `#diagonalHatch` / `#diagonalHatchHoliday`, which are
 * identical). Holidays come from the project calendar via `date-holidays`;
 * custom days off come from `project.non_working_days` (WI #5651).
 */
function drawShading(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
  start: DateTime,
  visFirst: number,
  visLast: number,
): void {
  const pattern = hatchPattern(ctx);
  if (!pattern) {
    return;
  }
  const height = Math.max(0, scene.map.maskSize.height);
  ctx.save();
  ctx.fillStyle = pattern;

  const fillDay = (i: number): void => {
    const x0 = scene.screenX(i);
    const x1 = scene.screenX(i + 1);
    if (x1 < -5 || x0 > scene.width + 5) {
      return;
    }
    ctx.fillRect(x0, START_Y, x1 - x0, height);
  };

  // Only test the visible day range (callers pass it); a whole-map weekday scan
  // was O(mapLength) every frame even at the deepest zoom.
  for (let i = visFirst; i <= visLast; i++) {
    const weekday = start.plus({ days: i }).weekday; // 6 = Sat, 7 = Sun
    if (weekday === 6 || weekday === 7) {
      fillDay(i);
    }
  }
  for (const pos of holidayPositions(scene)) {
    fillDay(pos);
  }
  // Custom days off are a second, independent source of shaded columns, kept
  // deliberately separate from the `date-holidays` path above (WI #5651). The
  // two may overlap — filling the same column twice with the same pattern is
  // invisible, so there is nothing to deduplicate.
  for (const pos of customDayOffPositions(scene)) {
    fillDay(pos);
  }
  ctx.restore();
}

// Holiday positions are expensive to derive (instantiate `date-holidays` + scan
// every year), so memoize by project / calendar / range; the bg layer repaints on
// every pan & zoom.
let holidayCache: { key: string; positions: number[] } | null = null;

/** Day-offsets of public holidays in range (MapTimeline.holidayDays). */
function holidayPositions(scene: SceneContext): number[] {
  const project = scene.project.project(scene.map.mapId);
  const calendar = project?.calendar;
  if (!calendar || !calendar.length || !project.start_date) {
    return [];
  }
  const totalDays = scene.io.mapLength;
  const key = `${scene.map.mapId}|${calendar.join(',')}|${project.start_date}|${totalDays}`;
  if (holidayCache?.key === key) {
    return holidayCache.positions;
  }

  const [country, state, region] = calendar;
  const hd = new Holidays({
    country,
    state: state || undefined,
    region: region || undefined,
  });
  const start = new Date(project.start_date);
  const end = new Date(start);
  end.setDate(end.getDate() + totalDays);

  const positions: number[] = [];
  for (let year = start.getFullYear(); year <= end.getFullYear(); year++) {
    for (const holiday of hd.getHolidays(year)) {
      if (holiday.type !== 'public') {
        continue;
      }
      const date = new Date(holiday.date);
      if (date >= start && date <= end) {
        positions.push(
          daysBetweenDates(project.start_date, date.toISOString()),
        );
      }
    }
  }
  holidayCache = { key, positions };

  return positions;
}

// Same memoization story as holidays: the bg layer repaints on every pan & zoom,
// so the range expansion must not run per frame. The key carries a fingerprint
// of the entries themselves (not just their count) so an edited date or a
// deleted entry repaints instead of serving a stale offset list.
let customDayOffCache: { key: string; positions: number[] } | null = null;

/**
 * Day-offsets of the project's custom non-working days (WI #5651).
 *
 * Deliberately parallel to `holidayPositions()` rather than folded into it: the
 * regional public-holiday calendar and a project's own days off are independent
 * sources that happen to render identically, and keeping them apart means the
 * `date-holidays` path is untouched by this feature.
 */
function customDayOffPositions(scene: SceneContext): number[] {
  const project = scene.project.project(scene.map.mapId);
  const nonWorkingDays = project?.non_working_days;
  if (!nonWorkingDays?.length || !project.start_date) {
    return [];
  }
  const totalDays = scene.io.mapLength;
  const fingerprint = nonWorkingDays
    .map((day) => `${day.id}:${day.start_date}:${day.end_date}`)
    .join(',');
  const key = `${scene.map.mapId}|${project.start_date}|${totalDays}|${fingerprint}`;
  if (customDayOffCache?.key === key) {
    return customDayOffCache.positions;
  }

  const positions: number[] = [];
  for (const day of nonWorkingDays) {
    const first = daysBetweenDates(project.start_date, day.start_date);
    const last = daysBetweenDates(project.start_date, day.end_date);
    if (!Number.isFinite(first) || !Number.isFinite(last) || last < first) {
      continue;
    }
    // Clamp to the map so a range running off either end still shades the part
    // that is on the map, instead of pushing thousands of off-map offsets that
    // `fillDay` would only discard.
    for (
      let pos = Math.max(first, 0);
      pos <= Math.min(last, totalDays);
      pos++
    ) {
      positions.push(pos);
    }
  }
  customDayOffCache = { key, positions };

  return positions;
}

function daysFrom(start: DateTime, other: DateTime): number {
  return Math.round(other.diff(start, 'days').days);
}
